import { LineTypeEnum } from 'src/app/enum/workbench/fabric-preview/line-type-enum';
import { ShapeTypeEnum } from 'src/app/enum/workbench/shape-type.enum';
import { ConfigurationEdge } from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration-edge';
import { ConfigurationPlan } from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration-plan';
import { ConfigurationSideCut } from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration-side-cut';
import { Point } from 'src/app/models/configurators-2d/applications/workbench/utils/point';
import { SideCut } from 'src/app/models/configurators-2d/applications/workbench/side-cut';

export type WorkbenchEdgeSide = 'top' | 'bottom' | 'left' | 'right' | 'arc' | 'diagonal' | 'circle';

/**
 * Tolérance (mm) unique pour l'inférence du côté d'un chant à partir de ses points.
 * Doit être utilisée partout (toggle, isActive, rendu, dédoublonnage) pour éviter
 * qu'une arête soit considérée active d'un côté mais non matchée par le toggle.
 */
export const WORKBENCH_EDGE_SIDE_TOLERANCE = 5;

export interface WorkbenchEdgeDefinition {
  side: WorkbenchEdgeSide;
  startPoint: { x: number, y: number };
  endPoint: { x: number, y: number };
  lineType: LineTypeEnum;
  length: number;
}

type EdgeLike = {
  id?: string;
  startPoint?: Point;
  endPoint?: Point;
  length?: number;
  side?: string;
  sideCutId?: number;
  sideCut?: SideCut;
};

export function getWorkbenchEdgeDefinition(plan: ConfigurationPlan, sideHint: string): WorkbenchEdgeDefinition | null {
  const L = plan.length;
  const W = plan.width;
  const diameter = Number(plan.diameter || W);
  const radius = diameter / 2;

  switch (plan.shape.shapeType) {
    case ShapeTypeEnum.Rectangle:
      return createStraightDefinition(sideHint, L, W, {
        top: [{ x: 0, y: W }, { x: L, y: W }],
        bottom: [{ x: 0, y: 0 }, { x: L, y: 0 }],
        left: [{ x: 0, y: 0 }, { x: 0, y: W }],
        right: [{ x: L, y: 0 }, { x: L, y: W }]
      });
    case ShapeTypeEnum.HalfMoon: {
      const r = W / 2;
      if (sideHint === 'arc' || sideHint === 'right') {
        return { side: 'arc', startPoint: { x: 0, y: 0 }, endPoint: { x: 0, y: 0 }, lineType: LineTypeEnum.curve, length: W };
      }
      return createStraightDefinition(sideHint, L, W, {
        top: [{ x: 0, y: W }, { x: L - r, y: W }],
        bottom: [{ x: 0, y: 0 }, { x: L - r, y: 0 }],
        left: [{ x: 0, y: 0 }, { x: 0, y: W }]
      });
    }
    case ShapeTypeEnum.Mushroom: {
      const adjX = Math.sqrt(Math.max(0, radius ** 2 - (W - radius) ** 2));
      const stopX = L - radius - adjX;
      if (sideHint === 'arc' || sideHint === 'right') {
        return {
          side: 'arc',
          startPoint: { x: 0, y: 0 },
          endPoint: { x: 0, y: 0 },
          lineType: LineTypeEnum.curve,
          length: diameter
        };
      }

      const topEndX = plan.isFlip ? (L - radius) : stopX;
      const bottomEndX = plan.isFlip ? stopX : (L - radius);

      return createStraightDefinition(sideHint, L, W, {
        top: [{ x: 0, y: W }, { x: topEndX, y: W }],
        bottom: [{ x: 0, y: 0 }, { x: bottomEndX, y: 0 }],
        left: [{ x: 0, y: 0 }, { x: 0, y: W }]
      });
    }
    case ShapeTypeEnum.Circle:
      if (sideHint === 'circle') {
        const circleDiameter = Number(plan.diameter || plan.width);
        return {
          side: 'circle',
          startPoint: { x: 0, y: 0 },
          endPoint: { x: 0, y: 0 },
          lineType: LineTypeEnum.curve,
          length: circleDiameter * 4
        };
      }
      return null;
    case ShapeTypeEnum.Angle:
      return createStraightDefinition(sideHint, L, W, {
        top: [{ x: 0, y: L }, { x: L, y: L }],
        bottom: [{ x: 0, y: 0 }, { x: W, y: 0 }],
        left: [{ x: 0, y: 0 }, { x: 0, y: L }],
        right: [{ x: L, y: L - W }, { x: L, y: L }],
        diagonal: [{ x: W, y: 0 }, { x: L, y: L - W }]
      });
    default:
      return null;
  }
}

export function inferWorkbenchEdgeSide(
  plan: ConfigurationPlan,
  edge: EdgeLike | null | undefined,
  tolerance = WORKBENCH_EDGE_SIDE_TOLERANCE
): WorkbenchEdgeSide | null {
  if (!edge) return null;

  const explicitSide = normalizeSide((edge as any).side);
  if (explicitSide) return explicitSide;

  const sp = edge.startPoint;
  const ep = edge.endPoint;
  if (!sp || !ep) return null;

  const L = plan.length;
  const W = plan.width;

  switch (plan.shape.shapeType) {
    case ShapeTypeEnum.Rectangle:
    case ShapeTypeEnum.HalfMoon:
    case ShapeTypeEnum.Mushroom:
      if (Math.abs(sp.y) < tolerance && Math.abs(ep.y) < tolerance) return 'bottom';
      if (Math.abs(sp.y - W) < tolerance && Math.abs(ep.y - W) < tolerance) return 'top';
      if (Math.abs(sp.x) < tolerance && Math.abs(ep.x) < tolerance) return 'left';
      if (Math.abs(sp.x - L) < tolerance && Math.abs(ep.x - L) < tolerance) return 'right';
      if (Math.abs(sp.x) < tolerance && Math.abs(sp.y) < tolerance && Math.abs(ep.x) < tolerance && Math.abs(ep.y) < tolerance) {
        return plan.shape.shapeType === ShapeTypeEnum.Rectangle ? null : 'arc';
      }
      return null;
    case ShapeTypeEnum.Circle:
      return 'circle';
    case ShapeTypeEnum.Angle:
      if (Math.abs(sp.y) < tolerance && Math.abs(ep.y) < tolerance) return 'bottom';
      if (Math.abs(sp.y - L) < tolerance && Math.abs(ep.y - L) < tolerance) return 'top';
      if (Math.abs(sp.x) < tolerance && Math.abs(ep.x) < tolerance) return 'left';
      if (Math.abs(sp.x - L) < tolerance && Math.abs(ep.x - L) < tolerance) return 'right';
      if (
        (Math.abs(sp.x - W) < tolerance && Math.abs(sp.y) < tolerance) ||
        (Math.abs(ep.x - W) < tolerance && Math.abs(ep.y) < tolerance)
      ) {
        return 'diagonal';
      }
      return null;
    default:
      return null;
  }
}

export function normalizePlanEdgeCollections(plan: ConfigurationPlan): void {
  const edges = Array.isArray(plan.configurationEdges) ? plan.configurationEdges : [];
  const sideCuts = Array.isArray(plan.configurationSideCuts) ? plan.configurationSideCuts : [];

  if (plan.materialThickness?.canHaveSideCuts) {
    plan.configurationSideCuts = normalizeEntries(plan, [...sideCuts, ...edges], 'sideCut') as ConfigurationSideCut[];
    plan.configurationEdges = [];
    return;
  }

  plan.configurationEdges = normalizeEntries(plan, [...edges, ...sideCuts], 'edge') as ConfigurationEdge[];
  plan.configurationSideCuts = [];
}

function normalizeEntries(
  plan: ConfigurationPlan,
  entries: EdgeLike[],
  target: 'edge' | 'sideCut'
): Array<ConfigurationEdge | ConfigurationSideCut> {
  const normalized: Array<ConfigurationEdge | ConfigurationSideCut> = [];
  const seen = new Set<string>();

  entries.forEach(entry => {
    const side = inferWorkbenchEdgeSide(plan, entry);
    const key = side ?? `${entry.startPoint?.x}_${entry.startPoint?.y}_${entry.endPoint?.x}_${entry.endPoint?.y}`;
    if (seen.has(key)) return;
    seen.add(key);

    const canonicalEntry = target === 'edge'
      ? createConfigurationEdge(plan, side, entry)
      : createConfigurationSideCut(plan, side, entry);

    if (canonicalEntry) {
      normalized.push(canonicalEntry);
    }
  });

  return normalized;
}

function createConfigurationEdge(
  plan: ConfigurationPlan,
  side: WorkbenchEdgeSide | null,
  source: EdgeLike
): ConfigurationEdge | null {
  const definition = side ? getWorkbenchEdgeDefinition(plan, side) : null;
  const startPoint = definition?.startPoint ?? source.startPoint;
  const endPoint = definition?.endPoint ?? source.endPoint;
  if (!startPoint || !endPoint) return null;

  const edge = new ConfigurationEdge(
    plan.length,
    plan.width,
    plan.shape.shapeType,
    startPoint,
    endPoint,
    plan.colorId || 0,
    plan.materialId || 0,
    plan.materialThickness?.id || 0,
    definition?.lineType ?? LineTypeEnum.straight,
    plan.diameter
  );

  if (source.id) edge.id = source.id;
  if (plan.materialThickness) edge.materialThickness = plan.materialThickness;
  if (side) (edge as any).side = side;
  // NB : on NE force PAS edge.length = definition.length ici.
  // ConfigurationEdge.calculateLength() est l'autorité de la longueur COMMERCIALE (à vendre)
  // et applique déjà les règles métier par forme/côté (ex. demi-lune haut/bas = longueur du plan).
  // definition.length est la longueur GÉOMÉTRIQUE du segment (utilisée pour le rendu/les sideCuts),
  // qui diffère de la longueur facturée (ex. 2320 vs 2800 sur un grand côté demi-lune).

  return edge;
}

function createConfigurationSideCut(
  plan: ConfigurationPlan,
  side: WorkbenchEdgeSide | null,
  source: EdgeLike
): ConfigurationSideCut | null {
  const definition = side ? getWorkbenchEdgeDefinition(plan, side) : null;
  const startPoint = definition?.startPoint ?? source.startPoint;
  const endPoint = definition?.endPoint ?? source.endPoint;
  if (!startPoint || !endPoint) return null;

  const sideCut = new ConfigurationSideCut(
    startPoint,
    endPoint,
    (source as any).sideCut ?? null,
    definition?.length ?? source.length ?? 0
  );

  if (source.id) sideCut.id = source.id;
  if ((source as any).sideCutId && !(sideCut as any).sideCutId) {
    (sideCut as any).sideCutId = (source as any).sideCutId;
  }
  if (side) (sideCut as any).side = side;

  return sideCut;
}

function createStraightDefinition(
  sideHint: string,
  planLength: number,
  planWidth: number,
  definitions: Partial<Record<'top' | 'bottom' | 'left' | 'right' | 'diagonal', [{ x: number, y: number }, { x: number, y: number }]>> 
): WorkbenchEdgeDefinition | null {
  const normalizedSide = normalizeSide(sideHint);
  if (!normalizedSide || normalizedSide === 'arc' || normalizedSide === 'circle') return null;

  const segment = definitions[normalizedSide as 'top' | 'bottom' | 'left' | 'right' | 'diagonal'];
  if (!segment) return null;

  const [startPoint, endPoint] = segment;
  const dx = endPoint.x - startPoint.x;
  const dy = endPoint.y - startPoint.y;
  const fallbackLength = Math.round(Math.hypot(dx, dy));
  const length = normalizedSide === 'top' || normalizedSide === 'bottom'
    ? Math.round(Math.abs(dx || planLength))
    : normalizedSide === 'left' || normalizedSide === 'right'
      ? Math.round(Math.abs(dy || planWidth))
      : fallbackLength;

  return {
    side: normalizedSide,
    startPoint,
    endPoint,
    lineType: LineTypeEnum.straight,
    length
  };
}

function normalizeSide(side: string | undefined | null): WorkbenchEdgeSide | null {
  if (side === 'top' || side === 'bottom' || side === 'left' || side === 'right' || side === 'arc' || side === 'diagonal' || side === 'circle') {
    return side;
  }
  return null;
}
